import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Mic, Music, Play } from 'lucide-react';
import { GENRES, getInitialStations } from '@/constants/stations';
import { useAudioStore } from '@/hooks/useAudioStore';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { fetchStreams } from '@/services/strapi';
import { getStreamColor, getSolidColor } from '@/utils/streamColor';
import { getStationDisplayName } from '@/utils/stationName';
import { maybeTransliterate } from '@/utils/transliterate';
import { ScrollingTrackText } from '@/components/ScrollingTrackText';
import type { RadioStation } from '@/types';

export const ExploreScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'de';
  const [streams, setStreams] = useState<RadioStation[]>(() => getInitialStations());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const { playStation } = useAudioPlayer();
  const { currentStation, currentTrack, isPlaying } = useAudioStore();

  useEffect(() => {
    fetchStreams(locale).then((s) => s.length > 0 && setStreams(s));
  }, [locale]);

  const filteredStations = useMemo(() => {
    return streams.filter((station) => {
      const matchesSearch =
        searchQuery === '' ||
        station.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        station.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesGenre =
        selectedGenre === 'all' ||
        station.genre.toLowerCase() === selectedGenre.toLowerCase();

      return matchesSearch && matchesGenre;
    });
  }, [streams, searchQuery, selectedGenre]);

  return (
    <div className="min-h-screen px-4 pt-6 pb-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <h1 className="text-3xl font-bold">Istraži</h1>
        <button className="w-12 h-12 rounded-full glass flex items-center justify-center hover:bg-accent-blue/20 transition-colors">
          <Mic className="w-5 h-5" />
        </button>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass rounded-2xl px-4 py-3 flex items-center gap-3 mb-6"
      >
        <Search className="w-5 h-5 text-text-muted" />
        <input
          type="text"
          placeholder={t('explore.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent text-white placeholder-text-muted outline-none"
        />
        <Mic className="w-5 h-5 text-text-muted" />
      </motion.div>

      {/* Genre Filter */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide"
      >
        {GENRES.map((genre) => {
          const isSelected = selectedGenre === genre.id;
          return (
            <motion.button
              key={genre.id}
              onClick={() => setSelectedGenre(genre.id)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-all text-white ${
                isSelected
                  ? 'animate-gradient-tab shadow-lg shadow-purple-500/30'
                  : 'glass text-text-secondary hover:bg-white/10'
              }`}
            >
              {t(`genres.${genre.id}`)}
            </motion.button>
          );
        })}
      </motion.div>

      {/* Stations List */}
      <div className="space-y-3">
        {filteredStations.length > 0 ? (
          filteredStations.map((station, index) => {
            const isCurrentStation = currentStation?.id === station.id;
            const isCurrentPlaying = isCurrentStation && isPlaying;
            const stationColor = getStreamColor(station.color, station.name);
            const stationSolidColor = getSolidColor(stationColor);

            return (
              <motion.div
                key={station.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                onClick={() => {
                  playStation(station);
                  navigate('/app/player');
                }}
                className={`glass rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-all border-l-4 ${
                  isCurrentPlaying ? 'ring-2 ring-white/50' : ''
                }`}
                style={{ borderLeftColor: isCurrentPlaying ? stationSolidColor : stationSolidColor + '99' }}
              >
                {station.artwork && station.artwork.startsWith('http') ? (
                  <img
                    src={station.artwork}
                    alt={station.name}
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                ) : (
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center"
                    style={{ background: stationColor }}
                  >
                    <Music className="w-7 h-7 text-white drop-shadow" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{getStationDisplayName(station.name)}</h3>
                  <p className="text-sm text-accent-cyan">{station.genre}</p>
                  <p className="text-xs text-text-muted">
                    {isCurrentStation && isPlaying && currentTrack ? (
                      <ScrollingTrackText
                        text={`${maybeTransliterate(currentTrack.artist, locale)}${currentTrack.artist ? ' - ' : ''}${maybeTransliterate(currentTrack.title, locale)}`}
                        className="text-text-muted uppercase"
                      />
                    ) : (
                      <span className="truncate block">{maybeTransliterate(station.description, locale)}</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    playStation(station);
                    navigate('/app/player');
                  }}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    isCurrentPlaying ? 'text-white' : 'glass hover:bg-white/10'
                  }`}
                  style={isCurrentPlaying ? { background: stationColor } : {}}
                >
                  <Play className={`w-5 h-5 ${isCurrentPlaying ? 'fill-current' : ''}`} />
                </button>
              </motion.div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <Music className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <p className="text-text-muted">{t('explore.noStations')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
